'use client';

import { useState } from 'react';
import { Star, Send, X } from 'lucide-react';

interface ReviewFormProps {
  onSubmit: (review: {
    author: string;
    rating: number;
    title: string;
    content: string;
  }) => void;
}

export function ReviewForm({ onSubmit }: ReviewFormProps) {
  const [rating, setRating] = useState(5);
  const [author, setAuthor] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!author || !title || !content) {
      alert('Please fill in all fields');
      return;
    }
    onSubmit({ author, rating, title, content });
    setAuthor('');
    setTitle('');
    setContent('');
    setRating(5);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-slate-700 bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-8 backdrop-blur transition-all duration-300"
    >
      <h3 className="mb-6 text-xl font-bold text-white">Share Your Thoughts</h3>

      {/* Rating */}
      <div className="mb-6">
        <label className="mb-3 block text-sm font-semibold text-slate-300">
          Your Rating
        </label>
        <div className="flex gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setRating(i + 1)}
              onMouseEnter={() => setHoveredRating(i + 1)}
              onMouseLeave={() => setHoveredRating(0)}
              className="transition-transform duration-200 hover:scale-110"
            >
              <Star
                size={32}
                className={`transition-all duration-200 ${
                  i < (hoveredRating || rating)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-slate-600'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Name */}
      <div className="mb-6">
        <label htmlFor="name" className="mb-2 block text-sm font-semibold text-slate-300">
          Your Name
        </label>
        <input
          id="name"
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Enter your name"
          className="w-full rounded-lg bg-slate-900/50 px-4 py-3 text-white placeholder-slate-500 border border-slate-700 transition-all duration-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
        />
      </div>

      {/* Title */}
      <div className="mb-6">
        <label htmlFor="title" className="mb-2 block text-sm font-semibold text-slate-300">
          Review Title
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sum up your thoughts in a few words"
          className="w-full rounded-lg bg-slate-900/50 px-4 py-3 text-white placeholder-slate-500 border border-slate-700 transition-all duration-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
        />
      </div>

      {/* Content */}
      <div className="mb-6">
        <label htmlFor="content" className="mb-2 block text-sm font-semibold text-slate-300">
          Your Review
        </label>
        <textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Share your detailed thoughts about this book..."
          rows={5}
          className="w-full rounded-lg bg-slate-900/50 px-4 py-3 text-white placeholder-slate-500 border border-slate-700 transition-all duration-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 resize-none"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          type="submit"
          className="group inline-flex items-center gap-2 rounded-lg bg-amber-500 px-6 py-3 font-semibold text-slate-900 transition-all duration-300 hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/50 hover:-translate-y-0.5 active:translate-y-0 active:shadow-amber-500/25"
        >
          <Send size={18} className="transition-transform group-hover:translate-x-1" />
          Post Review
        </button>
      </div>
    </form>
  );
}
